/**
 * NotificationForm Component
 * 
 * Form component for creating and editing portal notifications.
 * Follows the same patterns as ArticleForm for consistency.
 * 
 * Features:
 * - Rich text editor for notification content
 * - Target selection (All, Individual, WorkingGroup, Company, Role)
 * - Priority and type selection
 * - Expiry date picker
 * - Action URL for clickable notifications
 * - Validation and submission
 * 
 * @author By John Bellew - Infinia.ie - last updated 23/07/2025
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell,
    faTimes,
    faSave,
    faSpinner,
    faCalendar,
    faUsers,
    faUser,
    faBuilding,
    faGlobe,
    faExclamationTriangle,
    faInfoCircle,
    faNewspaper,
    faSync,
    faLink,
    faEye,
    faEyeSlash,
    faRefresh,
    faDownload,
    faClock
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import apiHelper from '../../utils/apiHelper';
import './NotificationForm.css';

function NotificationForm({ notification, onSave, onClose, isLoading, currentUser, viewMode = false }) {
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        notificationType: 'Info',
        priority: 'Medium',
        targetType: 'All',
        targetValue: '',
        expiryDate: '',
        actionUrl: ''
    });

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [isDirty, setIsDirty] = useState(false);

    // Read status state (for view mode)
    const [readStatusData, setReadStatusData] = useState(null);
    const [isLoadingReadStatus, setIsLoadingReadStatus] = useState(false);
    const [showReadUsers, setShowReadUsers] = useState(true);
    const [showUnreadUsers, setShowUnreadUsers] = useState(true);

    // Data for dropdowns
    const [workingGroups, setWorkingGroups] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [users, setUsers] = useState([]);
    const [isLoadingWorkingGroups, setIsLoadingWorkingGroups] = useState(false);
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    // Form options
    const notificationTypes = [
        { value: 'News', label: 'News', icon: faNewspaper },
        { value: 'Event', label: 'Event', icon: faCalendar },
        { value: 'Survey', label: 'Survey', icon: faUsers },
        { value: 'Alert', label: 'Alert', icon: faExclamationTriangle },
        { value: 'Update', label: 'Update', icon: faSync },
        { value: 'Info', label: 'Info', icon: faInfoCircle }
    ];

    const priorities = [
        { value: 'High', label: 'High Priority', color: '#ef4444' },
        { value: 'Medium', label: 'Medium Priority', color: '#f59e0b' },
        { value: 'Low', label: 'Low Priority', color: '#6b7280' }
    ];

    const targetTypes = [
        { value: 'All', label: 'Everyone', icon: faGlobe, description: 'Send to all portal users' },
        { value: 'Individual', label: 'Individual', icon: faUser, description: 'Send to specific user' },
        { value: 'Company', label: 'Company', icon: faBuilding, description: 'Send to company employees' },
        { value: 'WorkingGroup', label: 'Working Group (Coming Soon)', icon: faUsers, description: 'Feature coming soon', disabled: true }
    ];

    // Quill editor configuration - simplified toolbar
    const quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }]
        ]
    };

    const quillFormats = [
        'bold', 'italic', 'underline', 'list', 'bullet'
    ];

    // Initialize form data when notification prop changes
    useEffect(() => {
        if (notification) {
            setFormData({
                title: notification.title || '',
                message: notification.message || '',
                notificationType: notification.type || 'Info',
                priority: notification.priority || 'Medium',
                targetType: notification.targetType || 'All',
                targetValue: notification.targetValue || '',
                expiryDate: notification.expiryDate ? formatDateForInput(notification.expiryDate) : '',
                actionUrl: notification.actionUrl || ''
            });
        } else {
            // Reset form for new notification
            setFormData({
                title: '',
                message: '',
                notificationType: 'Info',
                priority: 'Medium',
                targetType: 'All',
                targetValue: '',
                expiryDate: '',
                actionUrl: ''
            });
        }
        setErrors({});
        setIsDirty(false);
    }, [notification]);

    // Load dropdown data on demand when target type changes
    useEffect(() => {
        if (formData.targetType === 'Individual' && users.length === 0) {
            loadUsers();
        } else if (formData.targetType === 'WorkingGroup' && workingGroups.length === 0) {
            loadWorkingGroups();
        } else if (formData.targetType === 'Company' && companies.length === 0) {
            loadCompanies();
        }
    }, [formData.targetType]);

    // Load read status data when in view mode
    useEffect(() => {
        if (viewMode && notification) {
            loadReadStatusData();
            // Clear dirty state in view mode
            setIsDirty(false);
        }
    }, [viewMode, notification]);



    /**
     * Load detailed read status data for the notification (view mode)
     */
    const loadReadStatusData = async () => {
        if (!notification) return;
        
        setIsLoadingReadStatus(true);
        try {
            const response = await apiHelper.get(`/api/graph/getNotificationReadStatus/${notification.id}`);
            
            if (response?.data?.success) {
                setReadStatusData(response.data);
            } else {
                throw new Error(response?.data?.error || 'Failed to load read status data');
            }
        } catch (error) {
            toast.error('Failed to load read status data');
            setReadStatusData({
                success: false,
                readUsers: [],
                unreadUsers: [],
                totalTargetUsers: 0,
                readCount: 0,
                unreadCount: 0
            });
        } finally {
            setIsLoadingReadStatus(false);
        }
    };

    /**
     * Refresh read status data
     */
    const handleRefreshReadStatus = async () => {
        await loadReadStatusData();
        toast.success('Read status refreshed');
    };

    /**
     * Export read status data as CSV
     */
    const handleExportReadStatus = () => {
        if (!readStatusData) return;

        const allUsers = [
            ...readStatusData.readUsers.map(user => ({ ...user, status: 'Read' })),
            ...readStatusData.unreadUsers.map(user => ({ ...user, status: 'Unread' }))
        ];

        const csvContent = [
            ['Name', 'Email', 'Company', 'Status', 'Read Date'].join(','),
            ...allUsers.map(user => [
                `"${user.name || ''}"`,
                `"${user.email || ''}"`,
                `"${user.company || ''}"`,
                user.status,
                user.status === 'Read' ? `"${formatDateForInput(user.readDate)}"` : ''
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `notification-${notification.id}-read-status.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success('Read status data exported');
    };



    /**
     * Load users data on demand
     */
    const loadUsers = async () => {
        setIsLoadingUsers(true);
        try {
            const usersResponse = await apiHelper.get('/api/graph/getUsers');
            if (usersResponse?.data) {
                setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
            }
        } catch (error) {
            setUsers([]);
            toast.error('Failed to load users');
        } finally {
            setIsLoadingUsers(false);
        }
    };

    /**
     * Load working groups data on demand
     */
    const loadWorkingGroups = async () => {
        setIsLoadingWorkingGroups(true);
        try {
            const groupsResponse = await apiHelper.get('/api/graph/getWorkingGroups');
            if (groupsResponse?.data) {
                setWorkingGroups(Array.isArray(groupsResponse.data) ? groupsResponse.data : []);
            }
        } catch (error) {
            setWorkingGroups([]);
            toast.error('Failed to load working groups');
        } finally {
            setIsLoadingWorkingGroups(false);
        }
    };

    /**
     * Load companies data on demand
     */
    const loadCompanies = async () => {
        setIsLoadingCompanies(true);
        try {
            const companiesResponse = await apiHelper.get('/api/graph/getCompanies');
            if (companiesResponse?.data) {
                setCompanies(Array.isArray(companiesResponse.data) ? companiesResponse.data : []);
            }
        } catch (error) {
            setCompanies([]);
            toast.error('Failed to load companies');
        } finally {
            setIsLoadingCompanies(false);
        }
    };

    /**
     * Format date for HTML date input
     */
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
        } catch (error) {
            return '';
        }
    };

    /**
     * Format date for display (view mode)
     */
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-IE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    /**
     * Check if notification is expired
     */
    const isExpired = (expiryDate) => {
        if (!expiryDate) return false;
        return new Date(expiryDate) < new Date();
    };

    /**
     * Get notification type icon
     */
    const getTypeIcon = (type) => {
        switch (type) {
            case 'News': return faNewspaper;
            case 'Event': return faCalendar;
            case 'Survey': return faUsers;
            case 'Alert': return faExclamationTriangle;
            case 'Update': return faSync;
            default: return faInfoCircle;
        }
    };

    /**
     * Get target type icon
     */
    const getTargetIcon = (targetType) => {
        switch (targetType) {
            case 'All': return faGlobe;
            case 'Individual': return faUser;
            case 'WorkingGroup': return faUsers;
            case 'Company': return faBuilding;
            default: return faUser;
        }
    };

    /**
     * Get priority badge
     */
    const getPriorityBadge = (priority) => {
        return <span className={`priority-badge priority-${priority?.toLowerCase()}`}>{priority}</span>;
    };

    /**
     * Handle form field changes
     */
    const handleChange = (field, value) => {
        // Don't allow changes in view mode
        if (viewMode) return;
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setIsDirty(true);
        
        // Clear field error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }

        // Clear targetValue when targetType changes
        if (field === 'targetType') {
            setFormData(prev => ({
                ...prev,
                targetValue: ''
            }));
        }
    };

    /**
     * Validate form data
     */
    const validateForm = () => {
        const newErrors = {};

        // Title is required
        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        // Message is required
        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        }

        // Target value validation
        if (formData.targetType !== 'All' && !formData.targetValue.trim()) {
            newErrors.targetValue = 'Target value is required for specific targeting';
        }

        // Email validation for Individual targeting (dropdown selection)
        if (formData.targetType === 'Individual' && formData.targetValue) {
            // Since we're using a dropdown, just verify it contains an @ symbol
            if (!formData.targetValue.includes('@')) {
                newErrors.targetValue = 'Please select a valid user';
            }
        }

        // URL validation for action URL (allow both full URLs and relative URLs)
        if (formData.actionUrl && formData.actionUrl.trim()) {
            const url = formData.actionUrl.trim();
            
            // Allow relative URLs (starting with /) or full URLs (with protocol)
            const isRelativeUrl = url.startsWith('/');
            const isFullUrl = url.startsWith('http://') || url.startsWith('https://');
            
            if (!isRelativeUrl && !isFullUrl) {
                newErrors.actionUrl = 'Please enter a valid URL (e.g., "/news/weekly/1910" or "https://example.com")';
            } else if (isFullUrl) {
                // Validate full URLs with URL constructor
                try {
                    new URL(url);
                } catch (error) {
                    newErrors.actionUrl = 'Please enter a valid URL';
                }
            }
        }

        // Expiry date validation
        if (formData.expiryDate) {
            const expiryDate = new Date(formData.expiryDate);
            const now = new Date();
            if (expiryDate <= now) {
                newErrors.expiryDate = 'Expiry date must be in the future';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Prevent submission in view mode
        if (viewMode) return;
        
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setIsSubmitting(true);
        try {
            // Prepare data for submission
            const submissionData = {
                ...formData,
                // Ensure empty strings become null for optional fields
                targetValue: formData.targetValue || null,
                expiryDate: formData.expiryDate || null,
                actionUrl: formData.actionUrl || null
            };

            // Show progress message for updates
            if (notification) {
                toast.info('Updating notification...', { autoClose: 1000 });
            }

            await onSave(submissionData);
            
            // Clear form state after successful save
            setIsDirty(false);
        } catch (error) {
            toast.error('Failed to save notification');
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Handle form close with unsaved changes warning
     */
    const handleClose = () => {
        // Only check for unsaved changes in edit mode
        if (!viewMode && isDirty && !window.confirm('You have unsaved changes. Are you sure you want to close?')) {
            return;
        }
        onClose();
    };

    /**
     * Get target value placeholder and options
     */
    const getTargetValueInput = () => {
        switch (formData.targetType) {
            case 'All':
                return null; // No input needed
            
            case 'Individual':
                return (
                    <div className="form-group">
                        <label htmlFor="targetValue">Select User *</label>
                        <select
                            id="targetValue"
                            value={formData.targetValue}
                            onChange={(e) => handleChange('targetValue', e.target.value)}
                            className={errors.targetValue ? 'error' : ''}
                            disabled={isLoadingUsers}
                        >
                            <option value="">
                                {isLoadingUsers ? 'Loading users...' : 'Select User'}
                            </option>
                            {users.map(user => (
                                <option key={user.email} value={user.email}>
                                    {user.name} ({user.email})
                                </option>
                            ))}
                        </select>
                        {errors.targetValue && <span className="error-message">{errors.targetValue}</span>}
                        {isLoadingUsers && (
                            <small className="field-help">
                                <FontAwesomeIcon icon={faSpinner} spin /> Loading users from user profiles...
                            </small>
                        )}
                    </div>
                );
            
            case 'WorkingGroup':
                return (
                    <div className="form-group">
                        <label htmlFor="targetValue">Working Group *</label>
                        <select
                            id="targetValue"
                            value={formData.targetValue}
                            onChange={(e) => handleChange('targetValue', e.target.value)}
                            className={errors.targetValue ? 'error' : ''}
                            disabled={isLoadingWorkingGroups}
                        >
                            <option value="">
                                {isLoadingWorkingGroups ? 'Loading working groups...' : 'Select Working Group'}
                            </option>
                            {workingGroups.map(group => (
                                <option key={group.id} value={group.name}>
                                    {group.name}
                                </option>
                            ))}
                        </select>
                        {errors.targetValue && <span className="error-message">{errors.targetValue}</span>}
                        {isLoadingWorkingGroups && (
                            <small className="field-help">
                                <FontAwesomeIcon icon={faSpinner} spin /> Loading working groups from user profiles...
                            </small>
                        )}
                    </div>
                );
            
            case 'Company':
                return (
                    <div className="form-group">
                        <label htmlFor="targetValue">Company *</label>
                        <select
                            id="targetValue"
                            value={formData.targetValue}
                            onChange={(e) => handleChange('targetValue', e.target.value)}
                            className={errors.targetValue ? 'error' : ''}
                            disabled={isLoadingCompanies}
                        >
                            <option value="">
                                {isLoadingCompanies ? 'Loading companies...' : 'Select Company'}
                            </option>
                            {companies.map(company => (
                                <option key={company} value={company}>
                                    {company}
                                </option>
                            ))}
                        </select>
                        {errors.targetValue && <span className="error-message">{errors.targetValue}</span>}
                        {isLoadingCompanies && (
                            <small className="field-help">
                                <FontAwesomeIcon icon={faSpinner} spin /> Loading companies from user profiles...
                            </small>
                        )}
                    </div>
                );
            
            default:
                return null;
        }
    };

    return (
        <div className="notification-form-overlay" onClick={handleClose}>
            <div className="notification-form-container" onClick={(e) => e.stopPropagation()}>
                <div className="notification-form-header">
                    <h2>
                        <FontAwesomeIcon icon={faBell} />
                        {viewMode ? 'Notification Details' : (notification ? 'Edit Notification' : 'Create New Notification')}
                    </h2>
                    {viewMode && (
                        <div className="header-actions">
                            <button 
                                className="btn btn-secondary btn-sm"
                                onClick={handleRefreshReadStatus}
                                disabled={isLoadingReadStatus}
                                title="Refresh read status"
                            >
                                <FontAwesomeIcon icon={faRefresh} spin={isLoadingReadStatus} />
                            </button>
                            <button 
                                className="btn btn-secondary btn-sm"
                                onClick={handleExportReadStatus}
                                disabled={!readStatusData}
                                title="Export read status"
                            >
                                <FontAwesomeIcon icon={faDownload} />
                            </button>
                        </div>
                    )}
                    <button 
                        type="button" 
                        className="close-btn"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="notification-form">
                    <div className="form-content">
                        {viewMode ? (
                            <>
                                {/* View Mode - Notification Details */}
                                <div className="notification-view-content">
                                    {/* Notification Info */}
                                    <div className="notification-info-section">
                                        <div className="notification-header">
                                            <div className="notification-title-row">
                                                <FontAwesomeIcon 
                                                    icon={getTypeIcon(notification.type)} 
                                                    className="type-icon"
                                                />
                                                <h3>{notification.title}</h3>
                                                {getPriorityBadge(notification.priority)}
                                                {isExpired(notification.expiryDate) && (
                                                    <span className="status-badge expired">Expired</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="notification-meta">
                                            <div className="meta-item">
                                                <FontAwesomeIcon icon={getTargetIcon(notification.targetType)} />
                                                <span>
                                                    {notification.targetType}
                                                    {notification.targetValue && ` (${notification.targetValue})`}
                                                </span>
                                            </div>
                                            <div className="meta-item">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                <span>Created: {formatDate(notification.created)}</span>
                                            </div>
                                            {notification.expiryDate && (
                                                <div className="meta-item">
                                                    <FontAwesomeIcon icon={faClock} />
                                                    <span>Expires: {formatDate(notification.expiryDate)}</span>
                                                </div>
                                            )}
                                            {notification.actionUrl && (
                                                <div className="meta-item">
                                                    <FontAwesomeIcon icon={faLink} />
                                                    <span>Action: {notification.actionUrl}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="notification-message">
                                            <h4>Message:</h4>
                                            <div dangerouslySetInnerHTML={{ __html: notification.message }} />
                                        </div>
                                    </div>

                                    {/* Read Status Statistics */}
                                    {isLoadingReadStatus ? (
                                        <div className="loading-state">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>Loading read status...</span>
                                        </div>
                                    ) : readStatusData && (
                                        <>
                                            <div className="read-status-stats">
                                                <div className="stat-card">
                                                    <div className="stat-value">{readStatusData.readCount}</div>
                                                    <div className="stat-label">
                                                        <FontAwesomeIcon icon={faEye} />
                                                        Read
                                                    </div>
                                                </div>
                                                <div className="stat-card">
                                                    <div className="stat-value">{readStatusData.unreadCount}</div>
                                                    <div className="stat-label">
                                                        <FontAwesomeIcon icon={faEyeSlash} />
                                                        Unread
                                                    </div>
                                                </div>
                                                <div className="stat-card">
                                                    <div className="stat-value">{readStatusData.totalTargetUsers}</div>
                                                    <div className="stat-label">
                                                        <FontAwesomeIcon icon={faUsers} />
                                                        Total Users
                                                    </div>
                                                </div>
                                                <div className="stat-card">
                                                    <div className="stat-value">
                                                        {readStatusData.totalTargetUsers > 0 
                                                            ? Math.round((readStatusData.readCount / readStatusData.totalTargetUsers) * 100)
                                                            : 0}%
                                                    </div>
                                                    <div className="stat-label">Read Rate</div>
                                                </div>
                                            </div>

                                            {/* User Lists */}
                                            {(readStatusData.readUsers.length > 0 || readStatusData.unreadUsers.length > 0) && (
                                                <div className="user-lists-container">
                                                    {/* Read Users */}
                                                    <div className="user-list-section">
                                                        <div className="user-list-header">
                                                            <h4>
                                                                <FontAwesomeIcon icon={faEye} />
                                                                Read Users ({readStatusData.readUsers.length})
                                                            </h4>
                                                            <button
                                                                type="button"
                                                                className={`toggle-btn ${showReadUsers ? 'active' : ''}`}
                                                                onClick={() => setShowReadUsers(!showReadUsers)}
                                                            >
                                                                {showReadUsers ? 'Hide' : 'Show'}
                                                            </button>
                                                        </div>
                                                        {showReadUsers && (
                                                            <div className="user-list">
                                                                {readStatusData.readUsers.length === 0 ? (
                                                                    <div className="empty-list">
                                                                        No users have read this notification yet
                                                                    </div>
                                                                ) : (
                                                                    readStatusData.readUsers.map((user, index) => (
                                                                        <div key={index} className="user-item read">
                                                                            <div className="user-info">
                                                                                <div className="user-name">{user.name}</div>
                                                                                <div className="user-email">{user.email}</div>
                                                                                {user.company && (
                                                                                    <div className="user-company">{user.company}</div>
                                                                                )}
                                                                            </div>
                                                                            <div className="read-date">
                                                                                {formatDate(user.readDate)}
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Unread Users */}
                                                    <div className="user-list-section">
                                                        <div className="user-list-header">
                                                            <h4>
                                                                <FontAwesomeIcon icon={faEyeSlash} />
                                                                Unread Users ({readStatusData.unreadUsers.length})
                                                            </h4>
                                                            <button
                                                                type="button"
                                                                className={`toggle-btn ${showUnreadUsers ? 'active' : ''}`}
                                                                onClick={() => setShowUnreadUsers(!showUnreadUsers)}
                                                            >
                                                                {showUnreadUsers ? 'Hide' : 'Show'}
                                                            </button>
                                                        </div>
                                                        {showUnreadUsers && (
                                                            <div className="user-list">
                                                                {readStatusData.unreadUsers.length === 0 ? (
                                                                    <div className="empty-list">
                                                                        All targeted users have read this notification!
                                                                    </div>
                                                                ) : (
                                                                    readStatusData.unreadUsers.map((user, index) => (
                                                                        <div key={index} className="user-item unread">
                                                                            <div className="user-info">
                                                                                <div className="user-name">{user.name}</div>
                                                                                <div className="user-email">{user.email}</div>
                                                                                {user.company && (
                                                                                    <div className="user-company">{user.company}</div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Edit Mode - Form Fields */}
                                {/* Title */}
                                <div className="form-group">
                                    <label htmlFor="title">Title *</label>
                                    <input
                                        type="text"
                                        id="title"
                                        value={formData.title}
                                        onChange={(e) => handleChange('title', e.target.value)}
                                        placeholder="Enter notification title"
                                        className={errors.title ? 'error' : ''}
                                        maxLength={255}
                                    />
                                    {errors.title && <span className="error-message">{errors.title}</span>}
                                </div>

                                {/* Type and Priority Row */}
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="notificationType">Type</label>
                                        <select
                                            id="notificationType"
                                            value={formData.notificationType}
                                            onChange={(e) => handleChange('notificationType', e.target.value)}
                                        >
                                            {notificationTypes.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="priority">Priority</label>
                                        <select
                                            id="priority"
                                            value={formData.priority}
                                            onChange={(e) => handleChange('priority', e.target.value)}
                                        >
                                            {priorities.map(priority => (
                                                <option key={priority.value} value={priority.value}>
                                                    {priority.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="form-group">
                                    <label htmlFor="message">Message *</label>
                                    <ReactQuill
                                        value={formData.message}
                                        onChange={(value) => handleChange('message', value)}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="Enter your notification message..."
                                        className={errors.message ? 'error' : ''}
                                    />
                                    {errors.message && <span className="error-message">{errors.message}</span>}
                                </div>

                                {/* Target Type */}
                                <div className="form-group">
                                    <label htmlFor="targetType">Send To</label>
                                    <div className="target-type-grid">
                                        {targetTypes.map(target => (
                                            <div
                                                key={target.value}
                                                className={`target-option ${formData.targetType === target.value ? 'selected' : ''} ${target.disabled ? 'disabled' : ''}`}
                                                onClick={() => !target.disabled && handleChange('targetType', target.value)}
                                            >
                                                <FontAwesomeIcon icon={target.icon} className="target-icon" />
                                                <div className="target-info">
                                                    <div className="target-label">{target.label}</div>
                                                    <div className="target-description">{target.description}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Target Value (conditional) */}
                                {getTargetValueInput()}

                                {/* Action URL and Expiry Date Row */}
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="actionUrl">Action URL (Optional)</label>
                                        <input
                                            type="text"
                                            id="actionUrl"
                                            value={formData.actionUrl}
                                            onChange={(e) => handleChange('actionUrl', e.target.value)}
                                            placeholder="/news/weekly/1910 or https://example.com"
                                            className={errors.actionUrl ? 'error' : ''}
                                        />
                                        {errors.actionUrl && <span className="error-message">{errors.actionUrl}</span>}
                                        <small className="field-help">
                                            URL to navigate when notification is clicked. Use relative URLs like "/news/weekly/1910" or full URLs like "https://example.com"
                                        </small>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="expiryDate">Expiry Date (Optional)</label>
                                        <input
                                            type="datetime-local"
                                            id="expiryDate"
                                            value={formData.expiryDate}
                                            onChange={(e) => handleChange('expiryDate', e.target.value)}
                                            className={errors.expiryDate ? 'error' : ''}
                                        />
                                        {errors.expiryDate && <span className="error-message">{errors.expiryDate}</span>}
                                        <small className="field-help">Notification will be hidden after this date</small>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Form Actions */}
                    {!viewMode && (
                        <div className="form-actions">
                            <button 
                                type="button" 
                                className="btn btn-secondary"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={isSubmitting || isLoading}

                            >
                                {isSubmitting ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        {notification ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} />
                                        {notification ? 'Update Notification' : 'Create Notification'}
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </form>


            </div>
        </div>
    );
}

export default NotificationForm; 